import React from 'react';
import {forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';

// add loader for monaco editor to prevent loading from CDN
import { loader } from "@monaco-editor/react";
import * as monaco from "monaco-editor";

import Editor from '@monaco-editor/react';

import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';

// This call forces the monaco editor to load from a local instance and not from the CDN
loader.config({ monaco });

// Now set up worker configuration to fix "unexpected usage" and worker config errors
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker'
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker'
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker'
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker'
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker'
import { currentUser, UserPreference } from 'system/InfegyUser';

self.MonacoEnvironment = {
  getWorker(_, label) {
    if (label === 'json') {
      return new jsonWorker()
    }
    if (label === 'css' || label === 'scss' || label === 'less') {
      return new cssWorker()
    }
    if (label === 'html' || label === 'handlebars' || label === 'razor') {
      return new htmlWorker()
    }
    if (label === 'typescript' || label === 'javascript') {
      return new tsWorker()
    }
    return new editorWorker()
  }
}
// end of worker configuration


interface QueryCodeEditorProps {
  height: number;
  content: string;

  language?: string;
  readOnly?: boolean;
}

function QueryCodeEditorPanelContent(props: QueryCodeEditorProps, ref: any ): JSX.Element {
  const editorRef = useRef(null);
  const user = currentUser();

  const [editorContent, setEditorContent] = useState<string>(props.content || "");

  const [editorLang, ] = useState(props.language || "json");

  const editorHeight = props.height || 500;

  const isDarkMode = user?.getPreference(UserPreference.THEME)?.toLowerCase().includes("dark");
  const monacoTheme:string = isDarkMode ? "booleanSearchThemeDark" : "booleanSearchTheme";

  // Update the editor content when the content prop changes
  useEffect(() => {
    setEditorContent(props.content);
  }, [props.content]);

  // Capture the reference to the editor once mounted
  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
    editor.onDidChangeModelContent((e: any) => {
      setEditorContent(editor.getValue());
    });
  }

  useImperativeHandle(ref, function getRef() {
    return {
      getContent() {
        return editorContent;
      }
    };
  }, [editorContent]);

  const theme = useTheme();

  return ( 
    <Box sx={{
      "borderRadius": theme?.general?.borderRadius,
      "border": `1px solid ${theme.colors.alpha.black[30]}`,
      p:0,
      overflow: "hidden"
    }}>
      <Editor
        theme={monacoTheme}
        options={{ 
          minimap: { enabled: false },
          lineNumbers: "off",
          fontSize: 18,
          readOnly: props.readOnly ?? false,
        }}

        height={`${editorHeight}px`}
        defaultLanguage={editorLang}
        defaultValue={props.content || ""}
        value={editorContent}
        onMount={handleEditorDidMount} />
    </Box>
  );
}

const QueryCodeEditorPanel = forwardRef(QueryCodeEditorPanelContent);

export default QueryCodeEditorPanel;